import { calculateEma } from "../indicators/ema.js";
import { calculateSma } from "../indicators/sma.js";

export const CurrentBarUpdateResult = Object.freeze({
  APPLIED: "applied",
  IGNORED: "ignored",
  RECONCILE: "reconcile"
});

// Historical data remains authoritative. This function mutates only a transient
// clone: it updates the current historical bar or appends an intraday provisional
// bar that is discarded as soon as authoritative history is reconciled.
export function applyPriceDataToCurrentBar(
  data,
  message,
  { timeframe, timeZone = "UTC", sessionMode = "regular" } = {}
) {
  if (!data?.length || message?.type !== "PRICEDATA" || !Number.isFinite(message.price)) {
    return CurrentBarUpdateResult.IGNORED;
  }
  const eventTimestamp = Number.isFinite(message.eventTimestampMs)
    ? message.eventTimestampMs
    : message.receiptTimestampMs;
  if (!Number.isFinite(eventTimestamp)) return CurrentBarUpdateResult.IGNORED;
  const index = data.length - 1;
  const barTimestamp = Number(data.timestamps[index]);
  if (eventTimestamp < barTimestamp) return CurrentBarUpdateResult.IGNORED;
  if (!sessionAllowed(message.session, sessionMode)) {
    return CurrentBarUpdateResult.IGNORED;
  }
  if (!eventBelongsToExistingBar(barTimestamp, eventTimestamp, timeframe, timeZone)) {
    const duration = intradayDurationMs(timeframe);
    if (duration !== null) {
      appendProvisionalIntradayBar(
        data,
        barTimestamp + Math.floor((eventTimestamp - barTimestamp) / duration) * duration,
        Number(message.price),
        message.size
      );
      return CurrentBarUpdateResult.APPLIED;
    }
    return CurrentBarUpdateResult.RECONCILE;
  }

  const price = Number(message.price);
  data.highs[index] = Math.max(Number(data.highs[index]), price);
  data.lows[index] = Math.min(Number(data.lows[index]), price);
  data.closes[index] = price;
  if (Number.isFinite(message.size) && message.size > 0) {
    data.volumes[index] = Math.max(0, Number(data.volumes[index])) + message.size;
  }
  data.sma20 = calculateSma(data.closes, 20);
  data.ema50 = calculateEma(data.closes, 50);
  return CurrentBarUpdateResult.APPLIED;
}

function appendProvisionalIntradayBar(data, timestamp, price, size) {
  data.timestamps = appendNumber(data.timestamps, timestamp);
  data.opens = appendNumber(data.opens, price);
  data.highs = appendNumber(data.highs, price);
  data.lows = appendNumber(data.lows, price);
  data.closes = appendNumber(data.closes, price);
  data.volumes = appendNumber(
    data.volumes,
    Number.isFinite(size) && size > 0 ? Number(size) : 0
  );
  data.sma20 = calculateSma(data.closes, 20);
  data.ema50 = calculateEma(data.closes, 50);
  data.length = data.timestamps.length;
}

function appendNumber(source, value) {
  const result = new Float64Array(source.length + 1);
  result.set(source);
  result[source.length] = value;
  return result;
}

export function eventBelongsToExistingBar(
  barTimestamp,
  eventTimestamp,
  timeframe,
  timeZone = "UTC"
) {
  const duration = intradayDurationMs(timeframe);
  if (duration !== null) return eventTimestamp < barTimestamp + duration;
  const unit = String(timeframe || "").trim().toUpperCase();
  if (/^\d+D$/.test(unit)) {
    const count = Number.parseInt(unit, 10);
    return calendarBucket(barTimestamp, timeZone, 'D', count) === calendarBucket(eventTimestamp, timeZone, 'D', count);
  }
  if (/^\d+[WM]$/.test(unit)) {
    const count = Number.parseInt(unit, 10);
    return calendarBucket(barTimestamp, timeZone, unit.at(-1), count)
      === calendarBucket(eventTimestamp, timeZone, unit.at(-1), count);
  }
  return false;
}

function intradayDurationMs(timeframe) {
  const match = /^(\d+)([mhH])$/.exec(String(timeframe || "").trim());
  if (!match) return null;
  const count = Number.parseInt(match[1], 10);
  return count * (match[2].toLowerCase() === "h" ? 3_600_000 : 60_000);
}

function calendarBucket(timestamp, timeZone, unit, count) {
  let parts;
  try {
    parts = new Intl.DateTimeFormat("en-US", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" })
      .formatToParts(new Date(timestamp));
  } catch {
    parts = new Intl.DateTimeFormat("en-US", { timeZone: 'UTC', year: "numeric", month: "2-digit", day: "2-digit" })
      .formatToParts(new Date(timestamp));
  }
  const number = name => Number(parts.find(part => part.type === name)?.value);
  const year = number('year'), month = number('month'), day = number('day');
  if (unit === 'M') return Math.floor((year * 12 + month - 1) / count);
  // Match DateOnly.DayNumber on the server (0001-01-01 is Monday).
  const dayNumber = Math.floor(Date.UTC(year, month - 1, day) / 86400000) + 719162;
  return Math.floor(dayNumber / (unit === 'W' ? 7 * count : count));
}

function sessionAllowed(providerSession, sessionMode) {
  const session = String(providerSession || "").trim().toLowerCase();
  if (!session) return true;
  if (sessionMode === "24-hours") return true;
  const extended = new Set(["pre", "premarket", "pre-market", "post", "postmarket", "post-market", "extended"]);
  return sessionMode === "extended" || !extended.has(session);
}
